import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import type { ApiDfsContest } from '@/api/dfs';
import {
  fetchActiveContests,
  fetchEnteredContests,
  fetchUpcomingContests,
} from '@/api/dfs';
import ContestCard from '@/components/dfs/ContestCard';
import SEO from '@/components/common/SEO';
import Spinner from '@/components/common/Spinner';
import HowItWorksCarousel from '@/components/scoring/HowItWorksCarousel';
import FilmRoomSection from '@/components/scoring/FilmRoomSection';
import TopPlayersSection from '@/components/scoring/TopPlayersSection';
import { useAuth } from '@/contexts/AuthContext';
import { mixpanelTrack } from '@/services/mixpanel';
import { compareContestsCombinedFirst, selectContestLobbyChoices } from '@/utils/contestLobby';

interface ContestFeeds {
  active: ApiDfsContest[];
  upcoming: ApiDfsContest[];
  entered: ApiDfsContest[];
}

const EMPTY_FEEDS: ContestFeeds = { active: [], upcoming: [], entered: [] };

const ContestsPage: React.FC = () => {
  const { token, isLoading: isAuthLoading, user } = useAuth();
  const navigate = useNavigate();
  const [feeds, setFeeds] = useState<ContestFeeds>(EMPTY_FEEDS);
  const [isLoading, setIsLoading] = useState(true);
  const [catalogError, setCatalogError] = useState<string | null>(null);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [showOtherLineups, setShowOtherLineups] = useState(false);
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    const id = window.setInterval(() => setNowMs(Date.now()), 60_000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    if (isAuthLoading) return;
    let cancelled = false;

    const loadLobby = async () => {
      setIsLoading(true);
      setCatalogError(null);

      const [activeResult, upcomingResult, enteredResult] = await Promise.allSettled([
        fetchActiveContests(token),
        fetchUpcomingContests(token, 60),
        token ? fetchEnteredContests(token) : Promise.resolve([]),
      ]);
      if (cancelled) return;

      const active = activeResult.status === 'fulfilled' ? activeResult.value : [];
      const upcoming = upcomingResult.status === 'fulfilled' ? upcomingResult.value : [];
      const entered = enteredResult.status === 'fulfilled' ? enteredResult.value : [];

      setFeeds({ active, upcoming, entered });
      setIsLoading(false);

      if (activeResult.status === 'rejected' && upcomingResult.status === 'rejected') {
        setCatalogError('We could not load weekend contests. Please try again.');
      }

      mixpanelTrack('Viewed Contest Lobby', {
        'User ID': user?.id,
        'Active Contests': active.length,
        'Upcoming Contests': upcoming.length,
        'Open Lineups': entered.filter(contest => !contest.is_finalized).length,
        'Entered Feed Failed': enteredResult.status === 'rejected',
      });
    };

    void loadLobby();
    return () => {
      cancelled = true;
    };
  }, [isAuthLoading, loadAttempt, token, user?.id]);

  const choices = useMemo(
    () => selectContestLobbyChoices(feeds.active, feeds.upcoming, feeds.entered, nowMs),
    [feeds, nowMs],
  );

  const selectedIds = useMemo(
    () => new Set(choices.map(choice => choice.contest.id)),
    [choices],
  );

  const otherOpenLineups = useMemo(
    () => feeds.entered
      .filter(contest => !contest.is_finalized && !selectedIds.has(contest.id))
      .sort(compareContestsCombinedFirst),
    [feeds.entered, selectedIds],
  );

  const handleHeroCtaClick = () => {
    const target = choices.find(choice => choice.mode !== 'entered') ?? choices[0];
    if (target) {
      mixpanelTrack('Hero CTA Clicked', {
        'Destination': 'builder',
        'Contest ID': target.contest.id,
        'Contest Scope': target.scope,
      });
      navigate(`/dfs/build/${target.contest.id}`);
      return;
    }

    document.getElementById('contest-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  if (isAuthLoading && isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-primary">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-primary px-3 pb-24 pt-4 text-gray-200 font-body md:px-8 md:pb-8 md:pt-6">
      <SEO
        title="Fantasy Futbol | Fantasy Football Draft Leagues for EPL & La Liga"
        description="Season-long fantasy draft leagues for the Premier League and La Liga (LALIGA), plus daily salary-cap contests - the fantasy football experience, for soccer."
        path="/contests"
      />

      {/* Desktop seasonal campaign. This deliberately stays out of the compact
          mobile/iOS lobby, where it previously crowded the primary contest
          action and the App Store screenshot surface. */}
      <Link
        to="/blog/2026-27-fantasy-epl-laliga-top-50-0923"
        className="-mx-8 -mt-6 mb-5 hidden min-h-11 items-center justify-center bg-accent-yellow px-4 text-center text-sm font-bold text-primary transition-colors font-heading hover:bg-yellow-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-white md:flex"
        onClick={() => mixpanelTrack('Clicked Season Preview Banner', {
          'Source Page': 'Contest Lobby',
          'Is Authenticated': Boolean(token),
          slug: '2026-27-fantasy-epl-laliga-top-50-0923',
        })}
        aria-label="Read the international break rankings of the top 50 Premier League and La Liga players so far"
      >
        International Break Rankings&nbsp;&nbsp;·&nbsp;&nbsp;See the Top 50 Premier League &amp; La Liga Players So Far&nbsp;→
      </Link>

      <div className="-mx-3 mb-5 px-4 pb-3 pt-1 md:-mx-8">
        <div className="mx-auto max-w-3xl text-center">
          <h1 className="mb-1 text-xl font-bold font-heading text-accent-yellow md:text-2xl">
            FANTASY FOOTBALL FOR SOCCER
          </h1>
          <p className="text-sm font-bold text-white md:text-base">
            Same Game. Same Strategy. Different Football.
          </p>

          {!token && (
            <>
              <div className="mt-3">
                <img
                  src="/hero-lineup-comparison-mobile.svg"
                  alt="Side-by-side NFL fantasy and Fantasy Futbol lineups — same game, different football."
                  className="mx-auto block h-auto w-full max-w-sm md:hidden"
                  width={400}
                  height={400}
                />
                <img
                  src="/hero-lineup-comparison.svg"
                  alt="Side-by-side NFL fantasy and Fantasy Futbol lineups showing identical salary totals and fantasy points."
                  className="mx-auto hidden h-auto w-full max-w-3xl md:block"
                  width={780}
                  height={415}
                />
              </div>

              <div className="mt-3 flex flex-wrap items-center justify-center gap-3">
                <button
                  type="button"
                  onClick={handleHeroCtaClick}
                  className="w-44 rounded-lg bg-accent-yellow px-6 py-2.5 text-sm font-bold uppercase tracking-wider text-primary transition-colors font-heading hover:bg-accent-yellow/90"
                >
                  Build Lineup
                </button>
                <button
                  type="button"
                  onClick={() => {
                    mixpanelTrack('How It Works Clicked', { 'User ID': user?.id });
                    navigate('/scoring');
                  }}
                  className="w-44 rounded-lg border-2 border-accent-yellow bg-transparent px-6 py-2.5 text-sm font-bold uppercase tracking-wider text-accent-yellow transition-colors font-heading hover:bg-accent-yellow/10"
                >
                  How to Play
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      <main id="contest-section" className="mx-auto max-w-2xl md:max-w-4xl">
        {isLoading ? (
          <div className="space-y-3" aria-label="Loading weekend contests">
            {[0, 1, 2].map(index => (
              <div
                key={index}
                className="h-[116px] animate-pulse rounded-xl border border-gray-700/50 bg-surface-card"
              />
            ))}
          </div>
        ) : catalogError ? (
          <div className="rounded-xl border border-red-500/40 bg-red-950/20 p-5 text-center">
            <p className="text-sm text-gray-200">{catalogError}</p>
            <button
              type="button"
              onClick={() => setLoadAttempt(attempt => attempt + 1)}
              className="mt-3 rounded-lg bg-accent-yellow px-5 py-2 text-sm font-bold text-primary font-heading"
            >
              Try Again
            </button>
          </div>
        ) : choices.length > 0 ? (
          <ul className="space-y-3">
            {choices.map(choice => (
              <ContestCard
                key={choice.contest.id}
                contest={choice.contest}
                mode={choice.mode}
                featured={choice.featured}
              />
            ))}
          </ul>
        ) : (
          <div className="rounded-xl border border-gray-700/60 bg-surface-card p-6 text-center">
            <p className="font-semibold text-white">The next weekend slate is being built.</p>
            <p className="mt-1 text-sm text-gray-400">Check back soon for EPL and La Liga contests.</p>
          </div>
        )}

        {token && (otherOpenLineups.length > 0 || feeds.entered.some(contest => contest.is_finalized)) && (
          <div className="mt-5 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs font-bold uppercase tracking-wider font-heading">
            {otherOpenLineups.length > 0 && (
              <button
                type="button"
                onClick={() => setShowOtherLineups(open => !open)}
                className="min-h-11 text-accent-yellow hover:text-yellow-300"
                aria-expanded={showOtherLineups}
              >
                {showOtherLineups ? 'Hide' : 'Show'} {otherOpenLineups.length} other lineup{otherOpenLineups.length === 1 ? '' : 's'}
              </button>
            )}
            {feeds.entered.some(contest => contest.is_finalized) && (
              <Link to="/profile#contest-history" className="flex min-h-11 items-center text-gray-300 hover:text-white">
                Contest History
              </Link>
            )}
          </div>
        )}

        {showOtherLineups && otherOpenLineups.length > 0 && (
          <section className="mt-2" aria-labelledby="other-lineups-heading">
            <h2 id="other-lineups-heading" className="mb-2 text-xs font-bold uppercase tracking-wider text-gray-400 font-heading">
              Other open lineups
            </h2>
            <ul className="space-y-3">
              {otherOpenLineups.map(contest => (
                <ContestCard key={contest.id} contest={contest} mode="entered" />
              ))}
            </ul>
          </section>
        )}
      </main>

      <div className="mx-auto mt-12 max-w-5xl md:mt-16">
        <HowItWorksCarousel />
        <div className="mt-8 md:mt-12">
          <FilmRoomSection surface="contests-lobby" />
        </div>
        {!token && <TopPlayersSection />}
      </div>

      <footer className="mx-auto mt-12 flex max-w-2xl flex-wrap items-center justify-center gap-x-3 text-xs text-gray-500 md:mt-16" aria-label="Legal and support links">
        <Link to="/privacy" state={{ from: '/contests' }} className="flex min-h-8 items-center hover:text-white">
          Privacy
        </Link>
        <span aria-hidden="true">·</span>
        <Link to="/support" state={{ from: '/contests' }} className="flex min-h-8 items-center hover:text-white">
          Support
        </Link>
        <span aria-hidden="true">·</span>
        <Link to="/contest-rules" className="flex min-h-8 items-center hover:text-white">
          Contest Rules
        </Link>
      </footer>
    </div>
  );
};

export default ContestsPage;
